/**
 * Client for ImageGenerationProcessor lambda function
 * @module ImageGenClient
 */

import fetch from 'cross-fetch';

/**
 * Client for interacting with ImageGenerationProcessor lambda
 */
export class ImageGenClient {
  /**
   * Initialize with base URL
   * @param {string} [baseUrl] - The API Gateway base URL
   */
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
    this.timeout = 30000; // 30 second timeout for individual requests
  }

  /**
   * Submit image generation request
   * @param {Object} requestData - Image generation request
   * @param {string} requestData.prompt - The prompt for image generation
   * @param {string} [requestData.additional_prompt] - Additional prompt text
   * @param {string} [requestData.batch_id] - Batch ID for tracking
   * @param {Object} [requestData.options] - Generation options
   * @returns {Promise<Object>} Response with job information
   */
  async generateImage(requestData) {
    if (!this.baseUrl) {
      throw new Error('ImageGenerationProcessor base URL not configured');
    }

    const generateUrl = `${this.baseUrl}/generate`;

    console.log('Calling ImageGenerationProcessor generate endpoint');
    console.debug('Request data:', requestData);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(generateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const jobId = result.data?.job_id;
      
      console.log(`Image generation queued: job_id=${jobId}`);
      return result;

    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('ImageGenerationProcessor request timed out');
        return { success: false, error: 'Request timed out' };
      }

      console.error(`ImageGenerationProcessor request failed: ${error.message}`);
      return { success: false, error: error.message };
    }
  }

  /**
   * Check status of image generation job
   * @param {string} jobId - Job ID to check
   * @returns {Promise<Object>} Response with job status
   */
  async checkStatus(jobId) {
    if (!this.baseUrl) {
      throw new Error('ImageGenerationProcessor base URL not configured');
    }

    const statusUrl = `${this.baseUrl}/status/${jobId}`;

    console.debug(`Checking status for job_id: ${jobId}`);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(statusUrl, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const status = result.data?.status || 'unknown';
      
      console.debug(`Job ${jobId} status: ${status}`);
      return result;

    } catch (error) {
      if (error.name === 'AbortError') {
        console.error(`Status check timed out for job ${jobId}`);
        return { success: false, error: 'Status check timed out' };
      }

      console.error(`Status check failed for job ${jobId}: ${error.message}`);
      return { success: false, error: error.message };
    }
  }

  /**
   * Poll for job completion
   * @param {string} jobId - Job ID to poll
   * @param {number} [maxWaitTime=300] - Maximum time to wait (seconds)
   * @param {number} [pollInterval=5] - Interval between polls (seconds)
   * @returns {Promise<Object>} Final job status
   */
  async pollForCompletion(jobId, maxWaitTime = 300, pollInterval = 5) {
    console.log(`Polling for completion of job ${jobId} (max wait: ${maxWaitTime}s)`);

    const startTime = Date.now();
    const maxWaitMs = maxWaitTime * 1000;
    const pollIntervalMs = pollInterval * 1000;

    while (Date.now() - startTime < maxWaitMs) {
      const statusResponse = await this.checkStatus(jobId);

      if (!statusResponse.success) {
        console.error(`Status check failed: ${JSON.stringify(statusResponse)}`);
        return statusResponse;
      }

      const status = statusResponse.data?.status || 'unknown';

      if (status === 'completed') {
        console.log(`Job ${jobId} completed successfully`);
        return statusResponse;
      }

      if (status === 'failed') {
        console.error(`Job ${jobId} failed`);
        return statusResponse;
      }

      if (['queued', 'processing'].includes(status)) {
        console.log(`Job ${jobId} still ${status}, waiting ${pollInterval}s...`);
        await this._sleep(pollIntervalMs);
      } else {
        console.warn(`Job ${jobId} has unknown status: ${status}`);
        await this._sleep(pollIntervalMs);
      }
    }

    // Timeout
    console.error(`Job ${jobId} polling timed out after ${maxWaitTime}s`);
    return { success: false, error: `Polling timed out after ${maxWaitTime}s` };
  }

  /**
   * Test if the lambda function is accessible
   * @returns {Promise<boolean>} True if accessible, false otherwise
   */
  async testConnection() {
    try {
      const testRequest = {
        prompt: 'test image for client validation',
        options: {
          model: 'dall-e-3',
          size: '1024x1024'
        }
      };

      const result = await this.generateImage(testRequest);
      return result.success || false;

    } catch (error) {
      console.error(`Connection test failed: ${error.message}`);
      return false;
    }
  }

  /**
   * Sleep utility function
   * @private
   * @param {number} ms - Milliseconds to sleep
   * @returns {Promise<void>}
   */
  _sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}